import React, { useState, useEffect, useRef } from 'react';
import { TaskExecution, Task } from '../../types/task';
import { StatusIndicator } from '../shared/StatusIndicators/StatusIndicator';
import { tasksApi } from '../../services/api/tasks';
import { useTaskManagement } from '../../hooks/useTaskManagement';
// THUB-186: timezone-aware shared formatter (was new Date(x).toLocaleString()).
import { formatDateTime, activeTimezone } from '../../utils/formatDateTime';

export interface TaskExecutionMonitorProps {
  task: Task;
  execution?: TaskExecution;
  onClose?: () => void;
  className?: string;
}

// THUB-198: removed PerformanceMetrics + DiagnosticInfo interfaces — the
// /performance-metrics and /diagnostics routes never existed (net::ERR_FAILED),
// and DiagnosticInfo described a Node-server shape (hostname/nodeVersion/memoryUsage)
// that is meaningless for Lambda. Monitor is now Status-only.

/**
 * THUB-226: remote-pickup listing diagnostics.
 *
 * These counters were computed on every listing and only written to CloudWatch, so
 * the common support question — "the partner says they sent the file, why didn't we
 * pick it up?" — could only be answered by someone with log access, which
 * value-stream teams deliberately do not have. Surfacing them here puts the answer
 * next to the execution.
 *
 * Renders nothing unless the execution actually carries pickup data, so it stays
 * invisible for delivery/hosted executions and for rows written before THUB-226.
 * The 0-matched case gets plain-language attribution rather than leaving the
 * operator to infer it from three numbers.
 */
function renderPickupDiagnostics(execution: TaskExecution) {
  const m = execution.metrics as TaskExecution['metrics'] & {
    totalFilesListed?: number;
    matchedFileCount?: number;
    skippedByPattern?: number;
    skippedByWatermark?: number;
    listingTruncated?: boolean;
    watermarkApplied?: string;
  };

  // THUB-278: distinguish "the field was ABSENT" from "the value is 0".
  //
  // The API transform used to default these to 0, which made a delivery/hosted
  // execution (no listing performed at all) indistinguishable from a pickup that
  // listed zero files. So the absence test must come BEFORE any defaulting.
  //
  // This also fixes a real hole in the old all-zero guard: a pickup that genuinely
  // listed 0 files IS meaningful ("the partner directory was empty when we looked") and
  // has its own explanatory hint below, but the guard silently suppressed precisely
  // that case.
  const hasListingData =
    m.totalFilesListed !== undefined ||
    m.matchedFileCount !== undefined ||
    m.skippedByPattern !== undefined ||
    m.skippedByWatermark !== undefined;

  // No listing happened (delivery execution, or a pre-THUB-226 row) -> render nothing
  // rather than a row of misleading zeros.
  if (!hasListingData) return null;

  const listed = m.totalFilesListed ?? 0;
  const matched = m.matchedFileCount ?? 0;
  const byPattern = m.skippedByPattern ?? 0;
  const byWatermark = m.skippedByWatermark ?? 0;

  // Why did we pick up nothing? Attribute it instead of making the operator guess.
  let zeroMatchHint: string | null = null;
  if (matched === 0) {
    if (listed === 0) {
      zeroMatchHint =
        'The partner directory was empty when we looked. Nothing was skipped — there was nothing there.';
    } else if (byPattern > 0 && byWatermark === 0) {
      zeroMatchHint =
        `All ${listed} file(s) were skipped because their names did not match this task's file pattern. ` +
        'Check the pattern against the actual filenames — matching is case-insensitive but the wildcards must still line up.';
    } else if (byWatermark > 0 && byPattern === 0) {
      zeroMatchHint =
        `All ${listed} file(s) were skipped as already-seen: they were older than this task's pickup cutoff` +
        (m.watermarkApplied ? ` (${formatDateTime(m.watermarkApplied, activeTimezone(), 'full')})` : '') +
        '. If these are genuinely new files, they were most likely collected by an earlier run.';
    } else {
      zeroMatchHint =
        `Of ${listed} file(s) listed, ${byPattern} did not match the file pattern and ` +
        `${byWatermark} were older than the pickup cutoff, so none were collected.`;
    }
  }

  const cell = (label: string, value: string) => (
    <div className="flex flex-col gap-1" key={label}>
      <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
        {label}
      </label>
      <span className="text-sm text-neutral-900 dark:text-neutral-100">{value}</span>
    </div>
  );

  return (
    <>
      {cell('Files Listed', listed.toLocaleString())}
      {cell('Matched', matched.toLocaleString())}
      {cell('Skipped (pattern)', byPattern.toLocaleString())}
      {cell('Skipped (already seen)', byWatermark.toLocaleString())}
      {m.watermarkApplied &&
        cell('Pickup Cutoff', formatDateTime(m.watermarkApplied, activeTimezone(), 'full'))}
      {m.listingTruncated && (
        <div className="flex flex-col gap-1 sm:col-span-2">
          <label className="text-xs font-medium text-warning-600 dark:text-warning-400 uppercase tracking-wider">
            Listing Truncated
          </label>
          <span className="text-sm text-warning-700 dark:text-warning-300">
            The partner directory held more files than one listing can return, so this run saw
            only part of it. The remainder should be collected by the next run.
          </span>
        </div>
      )}
      {zeroMatchHint && (
        <div className="flex flex-col gap-1 sm:col-span-2" data-testid="pickup-zero-match-hint">
          <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
            Why no files were collected
          </label>
          <span className="text-sm text-neutral-700 dark:text-neutral-300">{zeroMatchHint}</span>
        </div>
      )}
    </>
  );
}

const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'neutral' => {
  switch (status) {
    case 'completed':
    case 'success': return 'success';
    case 'running':
    case 'pending': return 'info';
    case 'failed':
    case 'error': return 'error';
    case 'cancelled': return 'warning';
    default: return 'neutral';
  }
};

const formatDate = (date: Date | string): string => {
  // THUB-186: was `new Intl.DateTimeFormat('en-US', {...}).format()` with NO timeZone,
  // which renders in the browser's zone and ignores the user's selection. It also fed a
  // bare `new Date(str)`, so a NAIVE backend timestamp was parsed as local time and shown
  // offset-wrong. 'precise' keeps the previous second-level granularity.
  //
  // Note this used Intl directly rather than toLocaleString, so a grep for
  // `toLocale*` — the one the audit and I both ran first — did not surface it.
  return formatDateTime(date, activeTimezone(), 'precise');
};

const formatDuration = (startTime: Date | string, endTime?: Date | string): string => {
  const start = typeof startTime === 'string' ? new Date(startTime) : startTime;
  if (!endTime) return 'Running...';
  const end = typeof endTime === 'string' ? new Date(endTime) : endTime;
  
  const duration = end.getTime() - start.getTime();
  const seconds = Math.floor(duration / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export function TaskExecutionMonitor({
  task,
  execution: initialExecution,
  onClose,
  className = ''
}: TaskExecutionMonitorProps) {
  // THUB-198: Monitor is Status-only now (Performance/Diagnostics/Logs subtabs removed).
  const [execution, setExecution] = useState<TaskExecution | undefined>(initialExecution);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // THUB-278: adopt a LATE-ARRIVING `execution` prop.
  //
  // `useState(initialExecution)` reads the prop only on the first render. The
  // containers fetch executions asynchronously, so if the Monitor tab is opened
  // before that fetch resolves the prop is still undefined at mount and the seed
  // would be discarded permanently — the tab would sit on "No active execution"
  // even though the data had since arrived.
  //
  // Deliberately one-directional: this only fills an EMPTY slot. It never
  // overwrites an execution already held, so a fresher WebSocket/poll update
  // cannot be clobbered by a staler value from the container's list.
  //
  // THUB-298 ACCEPTED EXCEPTION -- react-hooks/set-state-in-effect flags this, and the rule is
  // wrong here. I tried to derive it as `liveExecution ?? initialExecution` and the existing test
  // "does not let a late prop overwrite a newer execution already displayed" failed immediately:
  // that assertion requires the first non-empty prop to be LATCHED, so once a value is on screen a
  // different prop must not replace it. A latch is history-dependent by definition, and no pure
  // derivation of the current props can express "the first one I ever saw". Deriving it made the
  // panel follow the prop backwards, which is precisely the regression the test exists to catch
  // (the container's seed is `executions[0] ?? historyExecutions[0]`, which really does change as
  // those lists load).
  //
  // So the effect stays. The rule is now ON repo-wide with this single targeted exemption, rather
  // than off everywhere to accommodate one case where it is wrong.
  useEffect(() => {
    if (!initialExecution) return;
    // A deliberate latch that no pure derivation can express; enforced by an existing test. See
    // the full reasoning in the comment block above this effect.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setExecution((current) => current ?? initialExecution);
  }, [initialExecution]);

  const { subscribeToTask } = useTaskManagement({
    onExecutionUpdate: (updatedExecution) => {
      if (updatedExecution.taskId === task.taskId) {
        setExecution(updatedExecution);
      }
    }
  });

  const handleCancelExecution = async () => {
    if (!execution || execution.status !== 'running') return;
    
    try {
      setLoading(true);
      const cancelledExecution = await tasksApi.cancelTaskExecution(execution.executionId);
      setExecution(cancelledExecution);
    } catch (err) {
      setError('Failed to cancel execution');
      console.error('Error cancelling execution:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = subscribeToTask(task.taskId);
    return unsubscribe;
  }, [task.taskId, subscribeToTask]);

  // THUB-131: Hydrate execution state on mount when the task has a non-terminal
  // lastExecution but no local execution was passed in. This covers the F5/reload
  // case where the WebSocket event has already fired and wouldn't be seen again.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    if (execution !== undefined) return;
    const lastStatus = task.lastExecution?.status;
    if (lastStatus !== 'pending' && lastStatus !== 'running') return;

    hydratedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        // Task-scoped endpoint (Builder-allowed). The global /tasks/executions
        // endpoint requires system_admin and 403s for Builders.
        const executions = await tasksApi.getExecutionsForTask(task.taskId, 1);
        const first = executions?.[0];
        if (!cancelled && first) {
          setExecution(first);
        }
      } catch (err) {
        console.error('Error hydrating execution on mount:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [task.taskId, task.lastExecution?.status, execution]);

  // THUB-131: while execution is non-terminal, poll as a fallback in case the WebSocket
  // subscription misses an update.
  //
  // This used to be a fixed 5s interval capped at 60s (12 ticks). The cap was the bug: a transfer
  // running longer than a minute whose WebSocket update ALSO went missing left the panel showing
  // "running" forever, with nothing on screen to say tracking had stopped. Large PGP and Azure
  // transfers routinely exceed 60s, so the cliff sat right where it did the most harm.
  //
  // Raising the cap would only move the cliff, so there is no cap: polling now continues while the
  // execution is non-terminal and stops on a terminal status or unmount. To keep that from
  // becoming a request firehose on a genuinely stuck run, the interval BACKS OFF -- 5s while a
  // transfer is likely to finish, then progressively wider. A normal run sees exactly the old
  // cadence; only the long tail is cheaper than before.
  useEffect(() => {
    const status = execution?.status;
    const executionId = execution?.executionId;
    if (!executionId) return;
    if (status !== 'pending' && status !== 'running') return;

    /** Stay responsive early, then widen — the cost of an unbounded poll, not a hard stop. */
    const delayFor = (elapsedMs: number) => {
      if (elapsedMs < 60_000) return 5_000;
      if (elapsedMs < 5 * 60_000) return 15_000;
      return 30_000;
    };

    const startedAt = Date.now();
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    const schedule = () => {
      timeoutId = setTimeout(async () => {
        if (cancelled) return;
        try {
          // Use task-scoped endpoint (Builder-allowed) + match by executionId.
          const recent = await tasksApi.getExecutionsForTask(task.taskId, 10);
          const latest = recent.find((e) => e.executionId === executionId);
          if (latest && !cancelled) {
            setExecution(latest);
            if (latest.status !== 'pending' && latest.status !== 'running') {
              return; // terminal — stop rescheduling
            }
          }
        } catch (err) {
          // A failed poll must not end tracking; the next tick retries.
          console.error('Error polling execution status:', err);
        }
        if (!cancelled) schedule();
      }, delayFor(Date.now() - startedAt));
    };

    schedule();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [execution?.status, execution?.executionId, task.taskId]);

  const renderStatus = () => (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col md:flex-row justify-between items-start gap-8">
        <div>
          <h4 className="m-0 mb-4 text-lg font-semibold text-neutral-900 dark:text-neutral-100">
            {/* THUB-278: the panel now also shows the most recent COMPLETED run, so
                "Current Execution" would misdescribe it. Only claim "current" while the
                run is actually in flight. */}
            {execution && execution.status !== 'pending' && execution.status !== 'running'
              ? 'Latest Execution'
              : 'Current Execution'}
          </h4>
          {execution ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Execution ID
                </label>
                <span className="font-mono text-xs bg-neutral-100 dark:bg-slate-800 px-2 py-1 rounded">
                  {execution.executionId}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Status
                </label>
                <StatusIndicator
                  status={getStatusColor(execution.status)}
                  label={execution.status.charAt(0).toUpperCase() + execution.status.slice(1)}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Started
                </label>
                <span className="text-sm text-neutral-900 dark:text-neutral-100">{formatDate(execution.startTime)}</span>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Duration
                </label>
                <span className="text-sm text-neutral-900 dark:text-neutral-100">{formatDuration(execution.startTime, execution.endTime)}</span>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Files Processed
                </label>
                <span className="text-sm text-neutral-900 dark:text-neutral-100">{execution.metrics.filesProcessed.toLocaleString()}</span>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Data Transferred
                </label>
                <span className="text-sm text-neutral-900 dark:text-neutral-100">{formatBytes(execution.metrics.totalFileSize)}</span>
              </div>
              {execution.error?.errorMessage && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Error
                  </label>
                  <span className="text-sm text-danger-600 dark:text-danger-400">{execution.error.errorMessage}</span>
                </div>
              )}
              {renderPickupDiagnostics(execution)}
            </div>
          ) : (
            <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
              <p className="m-0">No active execution</p>
            </div>
          )}
        </div>

        <div>
          {execution && execution.status === 'running' && (
            <button
              onClick={handleCancelExecution}
              className="
                px-6 py-3 bg-danger-500 hover:bg-danger-600
                text-white border-none rounded-md
                text-sm font-medium cursor-pointer
                transition-colors duration-200
                disabled:bg-neutral-400 disabled:cursor-not-allowed
              "
              disabled={loading}
            >
              Cancel Execution
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="
          flex items-center gap-3 p-4
          bg-danger-50 dark:bg-danger-900/20
          border border-danger-200 dark:border-danger-800
          rounded-md text-danger-600 dark:text-danger-400 text-sm
        ">
          <span>⚠️</span>
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="
              ml-auto bg-transparent border-none
              text-danger-600 dark:text-danger-400 cursor-pointer
              text-xl p-0 w-6 h-6 flex items-center justify-center
            "
          >
            ×
          </button>
        </div>
      )}

      <div>
        <h4 className="m-0 mb-4 text-lg font-semibold text-neutral-900 dark:text-neutral-100">
          Real-time Progress
        </h4>
        {execution && execution.status === 'running' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="
              text-center p-4
              bg-neutral-50 dark:bg-slate-800
              rounded-md border border-neutral-200 dark:border-slate-700
            ">
              <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2">
                Files Processed
              </label>
              <div className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
                {execution.metrics.filesProcessed.toLocaleString()}
              </div>
            </div>
            <div className="
              text-center p-4
              bg-neutral-50 dark:bg-slate-800
              rounded-md border border-neutral-200 dark:border-slate-700
            ">
              <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2">
                Data Transferred
              </label>
              <div className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
                {formatBytes(execution.metrics.totalFileSize)}
              </div>
            </div>
            <div className="
              text-center p-4
              bg-neutral-50 dark:bg-slate-800
              rounded-md border border-neutral-200 dark:border-slate-700
            ">
              <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2">
                Elapsed Time
              </label>
              <div className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
                {formatDuration(execution.startTime)}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-neutral-500 dark:text-neutral-400 italic">
            Task is not currently running
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={`
      bg-white dark:bg-slate-900
      border border-neutral-200 dark:border-slate-700
      rounded-lg overflow-hidden
      ${className}
    `}>
      <div className="
        flex justify-between items-center
        px-6 py-4
        bg-neutral-50 dark:bg-slate-800
        border-b border-neutral-200 dark:border-slate-700
      ">
        <div>
          <h2 className="m-0 text-xl font-semibold text-neutral-900 dark:text-neutral-100">
            Task Execution Monitor
          </h2>
          <span className="text-sm text-neutral-500 dark:text-neutral-400">{task.name}</span>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="
              px-4 py-2 bg-neutral-100 dark:bg-slate-700
              border border-neutral-300 dark:border-slate-600
              rounded text-sm cursor-pointer
              text-neutral-800 dark:text-neutral-200
              transition-colors duration-200
              hover:bg-neutral-200 dark:hover:bg-slate-600
            "
          >
            Close
          </button>
        )}
      </div>

      {/* THUB-198: Monitor collapsed to Status-only — the Performance/Diagnostics/Logs
          subtabs called nonexistent routes (/performance-metrics, /diagnostics) or were
          static CloudWatch stubs. The tab strip is gone so no empty tabs remain. */}
      <div className="p-6">
        {renderStatus()}
      </div>
    </div>
  );
}